import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { postService } from "../services/api";
import { X } from "lucide-react";
import toast from "react-hot-toast";

const Editor: React.FC = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState<string>("en");

  const LANGUAGES = [
    { value: "en", label: "English" },
    { value: "es", label: "Español" },
    { value: "fr", label: "Français" },
    { value: "de", label: "Deutsch" },
    { value: "pt", label: "Português" },
    { value: "it", label: "Italiano" },
    { value: "ru", label: "Русский" },
    { value: "tr", label: "Türkçe" },
    { value: "pl", label: "Polski" },
    { value: "uk", label: "Українська" },
    { value: "nl", label: "Nederlands" },
    { value: "ro", label: "Română" },
    { value: "el", label: "Ελληνικά" },
    { value: "sv", label: "Svenska" },
    { value: "cs", label: "Čeština" },
    { value: "hu", label: "Magyar" },
    { value: "sh", label: "Srpskohrvatski" },
    { value: "bg", label: "Български" },
    { value: "fi", label: "Suomi" },
    { value: "da", label: "Dansk" },
    { value: "sk", label: "Slovenčina" },
    { value: "no", label: "Norsk" },
  ];
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    hashtags: [""],
  });
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageProcessing, setImageProcessing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Ref for hashtag input
  const hashtagInputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLTextAreaElement>(null);

  const applyFormat = (type: "bold" | "italic") => {
    const ta = contentRef.current;
    if (!ta) return;
    const start = ta.selectionStart || 0;
    const end = ta.selectionEnd || 0;
    const before = formData.content.slice(0, start);
    const selected = formData.content.slice(start, end);
    const after = formData.content.slice(end);
    const wrapper = type === "bold" ? "**" : "_";
    const next = `${before}${wrapper}${selected || ""}${wrapper}${after}`;
    setFormData({ ...formData, content: next });
    setTimeout(() => {
      ta.focus();
      const cursorPos = selected ? start + wrapper.length + selected.length + wrapper.length : start + wrapper.length;
      ta.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: "",
      });
    }
  };

  const handleHashtagChange = (index: number, value: string) => {
    const newHashtags = [...formData.hashtags];
    newHashtags[index] = value.replace(/^#/, "");
    setFormData({
      ...formData,
      hashtags: newHashtags,
    });
  };

  const addHashtag = () => {
    if (formData.hashtags.length < 3) {
      setFormData({
        ...formData,
        hashtags: [...formData.hashtags, ""],
      });
    }
  };

  const removeHashtag = (index: number) => {
    const newHashtags = formData.hashtags.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      hashtags: newHashtags,
    });
  };

  const convertImageToBW = (imageUrl: string): Promise<string> => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new window.Image();

      img.onload = () => {
        if (!ctx) {
          resolve(imageUrl);
          return;
        }

        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        for (let i = 0; i < data.length; i += 4) {
          const gray = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
          data[i] = gray;
          data[i + 1] = gray;
          data[i + 2] = gray;
        }

        ctx.putImageData(imageData, 0, 0);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const bwImageUrl = URL.createObjectURL(blob);
              resolve(bwImageUrl);
            } else {
              resolve(imageUrl);
            }
          },
          "image/jpeg",
          0.8
        );
      };

      img.src = imageUrl;
    });
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      return;
    }

    setImageProcessing(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const originalUrl = ev.target?.result as string;
      try {
        toast.loading("Converting image to black and white...", { duration: 2000 });
        const bwUrl = await convertImageToBW(originalUrl);
        setImagePreview(bwUrl);
        // Prefer compressed JPEG from canvas for upload to reduce backend failures
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            setImage(file);
            toast.dismiss();
            toast.success("✓ Image converted to black and white");
            setImageProcessing(false);
            return;
          }
          ctx.drawImage(img, 0, 0);
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" });
                setImage(compressedFile);
              } else {
                setImage(file);
              }
              toast.dismiss();
              toast.success("✓ Image converted to black and white");
              setImageProcessing(false);
            },
            "image/jpeg",
            0.85
          );
        };
        img.src = bwUrl;
      } catch (error) {
        console.error("Error converting image:", error);
        setImagePreview(originalUrl);
        setImage(file);
        toast.dismiss();
        toast.error("B&W conversion failed, will use automatic filter");
        setImageProcessing(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (formData.title.length < 10 || formData.title.length > 90) {
      newErrors.title = "Title must be between 10 and 90 characters";
    }

    if (formData.content.length < 150 || formData.content.length > 15000) {
      newErrors.content = "Content must be between 150 and 15,000 characters";
    }

    const validHashtags = formData.hashtags.filter((tag) => tag.trim() !== "");
    if (validHashtags.length < 1 || validHashtags.length > 3) {
      newErrors.hashtags = "You must include between 1 and 3 hashtags";
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      Object.values(validationErrors).forEach((error) => {
        toast.error(error);
      });
      return;
    }

    setLoading(true);

    try {
      const validHashtags = formData.hashtags.filter((tag) => tag.trim() !== "");

      const postData = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        hashtags: validHashtags,
        image: image || undefined,
        language,
      };

      const newPost = await postService.createPost(postData);
      toast.success("Article published successfully!");
      navigate(`/post/${newPost.id}`);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Error publishing article");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      Object.values(validationErrors).forEach((error) => {
        toast.error(error);
      });
      return;
    }

    setLoading(true);

    try {
      const validHashtags = formData.hashtags.filter((tag) => tag.trim() !== "");

      const postData = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        hashtags: validHashtags,
        image: image || undefined,
        language,
      };

      await postService.saveDraft(postData);
      toast.success("Article saved as draft in your profile. You must unarchive it to publish.");
      navigate("/profile");
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Error saving draft");
    } finally {
      setLoading(false);
    }
  };

  const charCount = formData.content.length;
  const validHashtags = formData.hashtags.filter((tag) => tag.trim() !== "");
  const hashtagsLeft = 3 - validHashtags.length;

  return (
    <div
      className="editor-container flex xl:flex-row flex-col items-stretch sm:items-center gap-[12px] sm:gap-4 md:gap-9 lg:gap-[55px] pt-[41px] pb-[41px] w-full mx-auto"
      style={{ justifyContent: "flex-start" }}
    >
      {/* Left Column - Title and Content */}
      <div className="inline-flex flex-col justify-start items-start gap-[8px] w-full md:min-w-[522px] md:h-[842px] max-w-full box-border">
        {/* Title */}
        <div
          className="flex flex-col justify-center"
          style={{
            alignSelf: "stretch",
            height: "59px",
            color: "#686868",
            fontSize: "36px",
            fontFamily: "Abhaya Libre",
            fontWeight: "400",
            lineHeight: "48px",
            wordWrap: "break-word",
          }}
        >
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Title (10 to 90 characters)"
            maxLength={90}
            className="w-full border-none outline-none bg-transparent"
            style={{
              color: "#686868",
              fontSize: "36px",
              fontFamily: "Abhaya Libre",
              fontWeight: "400",
              lineHeight: "48px",
            }}
          />
        </div>

        {/* Content Area */}
        {/* Content Preview */}

        {/* Content Editor */}
        <div
          className="ContentEditor flex flex-col justify-between items-stretch sm:items-center max-w-full box-border h-[380px] sm:h-[420px] md:h-[600px] lg:h-[772px] p-[8px] sm:p-[9px] md:p-[10px] lg:p-[10px]"
          style={{
            alignSelf: "stretch",
            background: "white",
            overflow: "hidden",
            borderRadius: "9px",
            outline: "1px #C6C6C6 solid",
            outlineOffset: "-1px",
          }}
        >
          <textarea
            name="content"
            ref={contentRef}
            value={formData.content}
            onChange={handleChange}
            placeholder="Write your article here..."
            maxLength={15000}
            className="w-full flex-1 border-none outline-none resize-none bg-transparent text-[15px] sm:text-[16px] md:text-[17px] lg:text-[18px] leading-[24px] sm:leading-[26px] md:leading-[28px] lg:leading-[30px]"
            style={{
              color: formData.content ? "#000" : "#ADAEBC",
              fontFamily: "Instrument Sans",
              fontWeight: "400",
              wordWrap: "break-word",
            }}
          />
          <div
            className="text-right h-[18px] sm:h-[20px] text-[12px] sm:text-[13px] md:text-[14px] leading-[16px] sm:leading-[18px] md:leading-[20px]"
            style={{
              alignSelf: "stretch",
              color: "#737373",
              fontFamily: "Instrument Sans",
              fontWeight: "400",
              wordWrap: "break-word",
            }}
          >
            {charCount} / 15,000 characters
          </div>
        </div>
        {/* Formatting Toolbar under editor */}
        {/* <div
          className="FormattingButtons"
          style={{
            alignSelf: "stretch",
            background: "rgba(0, 0, 0, 0)",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <div
            style={{
              width: "100%",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              borderRadius: "10px",
              padding: "6px",
              outline: "1px #C6C6C6 solid",
              outlineOffset: "-1px",
            }}
          >
            <button
              type="button"
              onClick={() => applyFormat("bold")}
              title="Bold"
              style={{
                width: "32px",
                height: "32px",
                background: "#EDEDED",
                borderRadius: "8px",
                border: "none",
                cursor: "pointer",
                color: "#171717",
                fontSize: "16px",
                fontFamily: "Abhaya Libre ExtraBold",
                fontWeight: "800",
              }}
            >
              B
            </button>
            <button
              type="button"
              onClick={() => applyFormat("italic")}
              title="Italic"
              style={{
                width: "32px",
                height: "32px",
                background: "#EDEDED",
                borderRadius: "8px",
                border: "none",
                cursor: "pointer",
                color: "#171717",
                fontSize: "16px",
                fontFamily: "Abhaya Libre ExtraBold",
                fontWeight: "800",
                fontStyle: "italic",
              }}
            >
              I
            </button>
          </div>
        </div> */}
      </div>

      {/* Right Column - Hashtags, Image, Buttons */}
      <div className="right-column flex flex-col justify-start items-start xl:w-fit w-full xl:space-y-0 space-y-4">
        {/* Hashtags Section */}
        <div
          className="w-full hashtags-section flex flex-col justify-start items-start gap-[9px]"
          style={{
            background: "rgba(0, 0, 0, 0)",
            borderTop: "1px #E5E5E5 solid",
          }}
        >
          <div style={{ alignSelf: "stretch", height: "20px", position: "relative", background: "rgba(0, 0, 0, 0)" }}>
            <div
              style={{
                width: "68px",
                height: "20px",
                left: "0px",
                top: "1px",
                position: "absolute",
                color: "#525252",
                fontSize: "14px",
                fontFamily: "Instrument Sans",
                fontWeight: "400",
                wordWrap: "break-word",
              }}
            >
              Hashtags
            </div>
          </div>

          {/* Input and Button Container */}
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <div
              style={{
                flex: "1",
                height: "42px",
                position: "relative",
                borderRadius: "76px",
                outline: "1px #D4D4D4 solid",
                outlineOffset: "-1px",
              }}
            >
              <input
                ref={hashtagInputRef}
                type="text"
                placeholder="Add up to 3 hashtags to categorize your article"
                onKeyPress={(e) => {
                  if (e.key === "Enter" && e.currentTarget.value.trim() && formData.hashtags.length < 3) {
                    const newTag = e.currentTarget.value.trim().replace(/^#/, "");
                    if (!formData.hashtags.includes(newTag)) {
                      setFormData({
                        ...formData,
                        hashtags: [...formData.hashtags.filter((tag) => tag.trim() !== ""), newTag],
                      });
                    }
                    e.currentTarget.value = "";
                  }
                }}
                style={{
                  width: "100%",
                  height: "42px",
                  paddingLeft: "12px",
                  paddingRight: "12px",
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  color: "#777777",
                  fontSize: "16px",
                  fontFamily: "Instrument Sans",
                  fontWeight: "400",
                  lineHeight: "24px",
                  borderRadius: "76px",
                }}
              />
            </div>

            {/* Add Hashtag Button */}
            <button
              onClick={() => {
                const input = hashtagInputRef.current;
                if (input && input.value.trim() && formData.hashtags.length < 3) {
                  const newTag = input.value.trim().replace(/^#/, "");
                  if (!formData.hashtags.includes(newTag)) {
                    setFormData({
                      ...formData,
                      hashtags: [...formData.hashtags.filter((tag) => tag.trim() !== ""), newTag],
                    });
                  }
                  input.value = "";
                }
              }}
              disabled={formData.hashtags.length >= 3}
              style={{
                paddingLeft: "16px",
                paddingRight: "16px",
                height: "42px",
                background: formData.hashtags.length >= 3 ? "#D4D4D4" : "#262626",
                borderRadius: "49px",
                justifyContent: "center",
                alignItems: "center",
                gap: "10px",
                display: "flex",
                border: "none",
                cursor: formData.hashtags.length >= 3 ? "not-allowed" : "pointer",
                opacity: formData.hashtags.length >= 3 ? 0.7 : 1,
                whiteSpace: "nowrap",
              }}
            >
              <div
                style={{
                  color: formData.hashtags.length >= 3 ? "#777777" : "white",
                  fontSize: "14px",
                  fontFamily: "Abhaya Libre ExtraBold",
                  fontWeight: "800",
                  wordWrap: "break-word",
                }}
              >
                Add hashtag
              </div>
            </button>
          </div>

          {/* Hashtag Pills */}
          <div
            style={{
              justifyContent: "flex-start",
              alignItems: "flex-start",
              gap: "9px",
              display: "inline-flex",
              flexWrap: "wrap",
            }}
          >
            {formData.hashtags
              .filter((tag) => tag.trim() !== "")
              .map((hashtag, index) => (
                <div
                  key={index}
                  style={{
                    paddingLeft: "16px",
                    paddingRight: "16px",
                    paddingTop: "9px",
                    paddingBottom: "9px",
                    borderRadius: "49px",
                    outline: "1px black solid",
                    outlineOffset: "-1px",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "10px",
                    display: "flex",
                  }}
                >
                  <div
                    style={{
                      color: "black",
                      fontSize: "14px",
                      fontFamily: "Abhaya Libre ExtraBold",
                      fontWeight: "800",
                      wordWrap: "break-word",
                    }}
                  >
                    #{hashtag}
                  </div>
                  <button
                    onClick={() => removeHashtag(index)}
                    style={{
                      width: "9px",
                      height: "12px",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <X size={9} color="black" />
                  </button>
                </div>
              ))}
          </div>

          {/* Hashtag Counter */}
          <div style={{ alignSelf: "stretch", height: "20px", position: "relative", background: "rgba(0, 0, 0, 0)" }}>
            <div
              style={{
                width: "261px",
                height: "20px",
                left: "22px",
                top: "0px",
                position: "absolute",
                color: "#525252",
                fontSize: "14px",
                fontFamily: "Instrument Sans",
                fontWeight: "600",
                lineHeight: "20px",
                wordWrap: "break-word",
              }}
            >
              {validHashtags.length} hashtags added.{" "}
              {hashtagsLeft > 0 ? `You can add ${hashtagsLeft} more.` : "Maximum reached."}
            </div>
          </div>
        </div>

        <div style={{ marginTop: "16px", width: "100%" }}>
          <label
            style={{
              display: "block",
              marginBottom: "4px",
              width: "109px",
              height: "20px",
              color: "#525252",
              fontSize: "14px",
              fontFamily: "Instrument Sans",
              fontWeight: "400",
              wordWrap: "break-word",
            }}
          >
            Language
          </label>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            style={{
              width: "100%",
              padding: "8px",
              borderRadius: "6px",
              fontFamily: "Instrument Sans",
              border: "1px solid #D4D4D4",
            }}
            required
          >
            {LANGUAGES.map((lang) => (
              <option key={lang.value} value={lang.value}>
                {lang.label}
              </option>
            ))}
          </select>
        </div>

        {/* Separator */}
        {/* <div
          className="hidden lg:block"
          style={{
            width: "100%",
            height: "1px",
            backgroundColor: "rgb(211, 211, 211)",
            marginTop: "17px",
            marginBottom: "17px",
          }}
        /> */}

        {/* Additional Information Section */}
        {/* <div
          className="w-full additional-info-section flex flex-col justify-start items-start gap-[9px]"
          style={{
            background: "rgba(0, 0, 0, 0)",
            gap: "9px",
          }}
        >
          <div
            style={{
              color: "#525252",
              fontSize: "14px",
              fontFamily: "Abhaya Libre",
              fontWeight: "400",
              wordWrap: "break-word",
            }}
          >
            Additional Information
          </div>
          <div
            className="Dropdown"
            style={{
              paddingLeft: "16px",
              paddingRight: "16px",
              paddingTop: "9px",
              paddingBottom: "9px",
              borderRadius: "49px",
              outline: "1px black solid",
              outlineOffset: "-1px",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              display: "inline-flex",
            }}
          >
            <div
              style={{
                color: "black",
                fontSize: "14px",
                fontFamily: "Abhaya Libre ExtraBold",
                fontWeight: "800",
                wordWrap: "break-word",
              }}
            >
              Select the language
            </div>

            <div
              className="Dropdown-icon"
              style={{ width: "12px", height: "12px", position: "relative", background: "rgba(0, 0, 0, 0)" }}
            >
              <div
                style={{
                  width: "12px",
                  height: "12px",
                  left: "0px",
                  top: "0px",
                  position: "absolute",
                  justifyContent: "center",
                  alignItems: "center",
                  display: "inline-flex",
                }}
              >
                <div
                  style={{
                    width: "8px",
                    height: "5px",
                    background: "black",
                  }}
                />
              </div>
            </div>
          </div>
        </div> */}

        {/* Separator */}
        <div
          className="hidden lg:block"
          style={{
            width: "100%",
            height: "1px",
            backgroundColor: "rgb(211, 211, 211)",
            marginTop: "17px",
            marginBottom: "17px",
          }}
        />

        {/* Featured Image Section */}
        <div
          className="w-full featured-image-section flex flex-col justify-start items-start gap-[8px]"
          style={{
            background: "rgba(0, 0, 0, 0)",
          }}
        >
          <div
            style={{
              width: "109px",
              height: "20px",
              color: "#525252",
              fontSize: "14px",
              fontFamily: "Instrument Sans",
              fontWeight: "400",
              wordWrap: "break-word",
            }}
          >
            Featured Image
          </div>
          <div
            className="featured-image-container xl:w-[578px] w-full"
            style={{
              height: "172px",
              position: "relative",
              background: "rgba(0, 0, 0, 0)",
              borderRadius: "6px",
              outline: "2px #D4D4D4 solid",
              outlineOffset: "-2px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
            onClick={() => document.getElementById("image-upload")?.click()}
          >
            <input
              id="image-upload"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ display: "none" }}
              disabled={imageProcessing}
            />
            <div
              style={{
                width: "239.34px",
                height: "80px",
                background: "rgba(0, 0, 0, 0)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "4px",
              }}
            >
              {/* logo image */}
              <img
                src="/Frame.svg"
                alt="Upload icon"
                style={{
                  width: "36px",
                  height: "36px",
                }}
              />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <div
                  style={{
                    background: "white",
                    borderRadius: "6px",
                    padding: "2px 8px",
                    textAlign: "center",
                    color: "#262626",
                    fontSize: "14px",
                    fontFamily: "Instrument Sans",
                    fontWeight: "400",
                  }}
                >
                  Upload a file or drag and drop
                </div>
              </div>
              <div
                style={{
                  textAlign: "center",
                  color: "#737373",
                  fontSize: "13px",
                  fontFamily: "Instrument Sans",
                  fontWeight: "400",
                  lineHeight: "16px",
                }}
              >
                Upload one black & white image (optional)
              </div>
            </div>
          </div>
        </div>

        {/* Separator */}
        <div
          className="hidden lg:block"
          style={{
            width: "100%",
            height: "1px",
            backgroundColor: "rgb(211, 211, 211)",
            marginTop: "17px",
            marginBottom: "17px",
          }}
        />

        {/* Image Preview Section */}
        <div
          className="w-full image-preview-section flex flex-col justify-start items-start gap-[10px]"
          style={{
            background: "rgba(0, 0, 0, 0)",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            gap: "10px",
            display: "flex",
          }}
        >
          <div
            style={{
              width: "103px",
              height: "20px",
              color: "#525252",
              fontSize: "14px",
              fontFamily: "Instrument Sans",
              fontWeight: "400",
              wordWrap: "break-word",
            }}
          >
            Image Preview
          </div>
          <div
            className="w-full h-[270px] relative flex items-center justify-between"
            style={{
              alignSelf: "stretch",
              background: imagePreview ? "#D4D4D4" : "#E5E5E5",
              borderRadius: "6px",
              overflow: "hidden",
            }}
          >
            {imagePreview ? (
              <img
                className="w-full h-full xl:object-cover object-cover filter grayscale"
                src={imagePreview}
                alt="Preview"
                style={{
                  width: "100%",
                  height: "100%",
                  filter: "grayscale(100%)",
                }}
              />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center"
                style={{
                  color: "#A3A3A3",
                  fontSize: "14px",
                  fontFamily: "Instrument Sans",
                  fontWeight: "400",
                  textAlign: "center",
                }}
              >
                No image selected
              </div>
            )}
          </div>
          {imagePreview && (
            <div style={{ marginTop: "10px" }}>
              <button
                type="button"
                onClick={() => {
                  setImage(null);
                  setImagePreview(null);
                }}
                style={{
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  paddingTop: "9px",
                  paddingBottom: "9px",
                  borderRadius: "49px",
                  outline: "1px black solid",
                  outlineOffset: "-1px",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Remove image
              </button>
            </div>
          )}
        </div>

        {/* Separator */}
        <div
          className="hidden lg:block"
          style={{
            width: "100%",
            height: "1px",
            backgroundColor: "rgb(211, 211, 211)",
            marginTop: "17px",
            marginBottom: "17px",
          }}
        />

        {/* Action Buttons */}
        <div style={{ justifyContent: "flex-start", alignItems: "flex-start", gap: "17px", display: "inline-flex" }}>
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              paddingLeft: "16px",
              paddingRight: "16px",
              paddingTop: "9px",
              paddingBottom: "9px",
              background: "#262626",
              borderRadius: "49px",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              display: "flex",
              border: "none",
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
            }}
          >
            <div
              style={{
                color: "white",
                fontSize: "14px",
                fontFamily: "Abhaya Libre ExtraBold",
                fontWeight: "800",
                wordWrap: "break-word",
              }}
            >
              {loading ? "Publishing..." : "Post"}
            </div>
          </button>
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={loading}
            style={{
              paddingLeft: "16px",
              paddingRight: "16px",
              paddingTop: "9px",
              paddingBottom: "9px",
              borderRadius: "49px",
              outline: "1px black solid",
              outlineOffset: "-1px",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              display: "flex",
              background: "transparent",
              border: "none",
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
            }}
          >
            <div
              style={{
                color: "black",
                fontSize: "14px",
                fontFamily: "Abhaya Libre ExtraBold",
                fontWeight: "800",
                wordWrap: "break-word",
              }}
            >
              {loading ? "Saving..." : "Save as draft"}
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Editor;
